<template>
  <div class="cargo-cascader">
    <el-cascader
      ref="elCascader"
      :value="innerValue"
      :disabled="disabled"
      :options="options"
      :filterable="filterable"
      clearable
      :class="canCopy?'can-copy':''"
      :placeholder="placeholder"
      :props="cascaderProps"
      :show-all-levels="staticOptions ? true : false"
      @keydown.native.enter.prevent="onEnter"
      @input="onInput"
      @change="onChange"
    />
    <el-tooltip v-if="canCopy" :content="$t('CargoMsg.Cargo_Copy')" placement="top">
      <el-link class="copy-icon" :underline="false" @click="handleCopy">
        <i class="el-icon-document-copy" />
      </el-link>
    </el-tooltip>
  </div>
</template>

<script>
import { AdvanceSelect, getCacheData } from '@/utils/class/AdvanceSelect'
import { deepCopy } from '@/utils/commonUtil'

export default {
  name: 'CargoCascader',
  props: {
    value: { type: [String, Number, Boolean], default: '' },
    disabled: { type: Boolean, default: false }, // 不可编辑
    selectType: { type: String, default: '' }, // 下拉控件类型(缓存key)
    filterable: { type: Boolean, default: false }, // 是否允许过滤
    groupField: { type: String, default: '' }, // 分组字段
    groupLabelMap: { type: Object, default: null }, // 分组字段label映射
    tagName: { type: String, default: '' }, // 选项用来显示的字段名
    tagValue: { type: String, default: '' }, // 选项值的字段名
    placeholder: { type: String, default: '' },
    methodParams: {
      type: Object, default: () => {
        return {}
      }
    }, // 过滤条件
    filterNullKey: { type: Boolean, default: false }, // 是否过滤分组key为空的数据
    railwayCommonDataSource: { type: Boolean, default: true }, // 铁进铁出是否共用一个数据源
    staticOptions: { type: [Array, Promise], default: null } // 静态数据源
  },
  data() {
    return {
      innerValue: [],
      advanceSelect: null,
      options: []
    }
  },
  computed: {
    canCopy() {
      return Boolean(this.value)
    },
    cascaderProps() {
      // 如果有staticOptions，使用特殊配置
      if (this.staticOptions) {
        return {
          // 只允许选择叶子节点，不能选择中间层级
          checkStrictly: false,
          // 指定选项的值为选项对象的某个属性值
          value: 'value',
          // 指定选项标签为选项对象的某个属性值
          label: 'label',
          // 指定选项的子选项为选项对象的某个属性值
          children: 'children',
          // 返回完整路径，用于显示所有级别
          emitPath: true,
          // 选项分隔符
          separator: ' / '
        }
      }
      // 默认配置（原有逻辑）
      return {
        value: 'value',
        label: 'label',
        children: 'children'
      }
    }
  },
  watch: {
    value: {
      immediate: true,
      handler() {
        if (this.staticOptions) {
          // 静态数据源时，如果value是单个值，不要重新设置innerValue
          // 因为innerValue应该保持完整的路径数组用于显示
          if (Array.isArray(this.value)) {
            this.innerValue = this.value
          } else if (this.value && this.innerValue.length === 0) {
            // 只有在innerValue为空时才设置，避免覆盖完整路径
            this.innerValue = [this.value]
          } else if (!this.value) {
            this.innerValue = []
          }
        } else if (this.groupField) {
          this.innerValue = [undefined, this.value]
        } else {
          this.innerValue = [this.value]
        }
      }
    },
    selectType: {
      immediate: true,
      handler() {
        if (this.selectType) {
          this.advanceSelect = new AdvanceSelect(this.selectType, {}, null, null, { includeAllItem: false })
        }
        this.initOptions()
      }
    },
    staticOptions: {
      immediate: true,
      handler() {
        this.initOptions()
      }
    },
    groupField() {
      this.initOptions()
    },
    groupLabelMap() {
      this.initOptions()
    },
    tagName() {
      this.initOptions()
    },
    tagValue() {
      this.initOptions()
    }
  },
  methods: {
    async initOptions() {
      // 如果有静态数据源，优先使用静态数据源
      if (this.staticOptions) {
        let staticData = this.staticOptions
        // 如果是Promise，等待解析
        if (staticData instanceof Promise) {
          staticData = await staticData
        }
        this.options = staticData || []
        return
      }

      // 如果没有selectType，直接返回
      if (!this.selectType || !this.advanceSelect) {
        this.options = []
        return
      }

      const res = await getCacheData(this.selectType, this.advanceSelect, '')
      let dataList = res.data
      for (const filterKey in this.methodParams) {
        const filterValue = this.methodParams[filterKey]
        dataList = this.filterData(filterKey, filterValue, dataList)
      }
      const ownTagName = this.tagName || this.advanceSelect.getTagName()
      const ownTagValue = this.tagValue || this.advanceSelect.getTagValue()
      if (this.groupField) {
        this.options = []
        const map = new Map()
        let data = []
        // 铁出、铁进使用统一数据源(铁出过滤出来，复制一份给铁进，uuid加上‘RI_’来区分)
        if (this.railwayCommonDataSource && Object.keys(this.groupLabelMap).includes('RI') && Object.keys(this.groupLabelMap).includes('RE')) {
          const reItems = deepCopy(dataList.filter(item => item[this.groupField] === 'RE'))
          reItems.map(item => {
            item[this.groupField] = 'RI'
            item[ownTagValue] = 'RI_' + item[ownTagValue]
            return item
          })
          data = dataList.concat(reItems)
        } else {
          data = dataList
        }
        for (const item of data) {
          // 支持 fba
          let groupValue = item[this.groupField]
          if (item.fba && ['SE', 'AE', 'RE'].indexOf(groupValue) > -1) {
            groupValue = 'FBA_' + groupValue
          }
          let obj = map.get(groupValue)
          // 分组的map不为空，但是根据数据源分组出来的key值在定义分组的map中为空 数据不显示
          if (this.filterNullKey && this.groupLabelMap && !this.groupLabelMap[groupValue]) {
            continue
          }
          if (!obj) {
            let label = ''
            if (this.groupLabelMap && this.groupLabelMap[groupValue]) {
              label = this.groupLabelMap[groupValue]
            } else if (groupValue === 'FBA_SE') {
              label = this.$t('CargoMsg.Cargo_FBA_SE')
            } else if (groupValue === 'FBA_AE') {
              label = this.$t('CargoMsg.Cargo_FBA_AE')
            } else if (groupValue === 'FBA_RE') {
              label = this.$t('CargoMsg.Cargo_FBA_RE')
            } else {
              label = groupValue
            }
            obj = {
              label: label,
              value: this.groupValue,
              children: []
            }
            map.set(groupValue, obj)
            this.options.push(obj)
          }
          obj.children.push({
            label: item[ownTagName],
            value: item[ownTagValue]
          })
        }
      } else {
        this.options = Array.from(dataList, p => { return { label: p[ownTagName], value: p[ownTagValue] } })
      }
    },
    //
    filterData(filterKey, filterValue, dateList) {
      const list = []
      for (const index in dateList) {
        const item = dateList[index]
        const value = item[filterKey]
        if (value && value === filterValue) {
          list.push(item)
        }
      }
      return list
    },
    onEnter() {
      this.$emit('enter')
    },
    onInput(val) {
      if (this.staticOptions) {
        // 静态数据源时，处理完整路径
        if (val && val.length > 0) {
          // 返回最后一级的值，但保持完整路径用于显示
          this.$emit('input', val[val.length - 1])
        } else {
          this.$emit('input', '')
        }
      } else {
        // 原有逻辑
        if (val && val.length > 0) {
          this.$emit('input', val[val.length - 1])
        } else {
          this.$emit('input', '')
        }
      }
    },
    onChange(val) {
      if (this.staticOptions) {
        // 静态数据源时的处理逻辑
        if (val && val.length > 0) {
          // 保存完整路径用于显示
          this.innerValue = val
          // val是完整路径数组，返回最后一级的值
          const selectedValue = val[val.length - 1]
          this.$emit('input', selectedValue)
          this.$emit('change', val)
        } else {
          this.innerValue = []
          this.$emit('input', '')
          this.$emit('change', [])
        }
      } else {
        // 原有逻辑
        if (val && val.length > 0) {
          const selectedValue = Array.isArray(val) ? val[val.length - 1] : val
          this.$emit('input', selectedValue)
          this.$emit('change', val)
        } else {
          this.$emit('input', '')
          this.$emit('change', [])
        }
      }
    },
    handleCopy() {
      const input = document.createElement('input')
      document.body.appendChild(input)
      input.setAttribute('value', this.$refs.elCascader.inputValue)
      input.select()
      if (document.execCommand('copy')) {
        document.execCommand('copy')
        this.$message.success(this.$t('SeaExport.CopySuccess'))
      }
      document.body.removeChild(input)
    }
  }
}
</script>

<style lang="scss" scoped>
.cargo-cascader ::v-deep {
  width: 100%;
  .el-cascader {
    width: 100%;
    &.can-copy .el-input__suffix {
      right: 18px !important;
    }
  }
}
.copy-icon {
  position: absolute;
  right: 5px;
}
</style>
